import React from 'react';
import { createRoot } from 'react-dom/client';
import { SycaiLoadingIndicator } from './SycaiLoadingIndicator';

const SPLASH_ELEMENT_ID = 'sycai-boot-splash';

const appWindow = window as unknown as {
  PUBLIC_URL?: string;
  config?: { routerBasename?: string };
};

/** The work list is the only route that renders at the router root; every mode nests below it. */
function isStudyListRoute(): boolean {
  const basename = String(appWindow.config?.routerBasename ?? '/').replace(/\/+$/, '');
  const path = window.location.pathname.replace(/\/+$/, '');

  return path === basename;
}

function studyListBackgroundImage(): string {
  const publicUrl = appWindow.PUBLIC_URL || '/';

  return (
    `linear-gradient(rgba(19, 24, 78, 0.45), rgba(19, 24, 78, 0.45)), ` +
    `url(${publicUrl}assets/background.png)`
  );
}

/**
 * Resolves once the splash has actually been painted. Redirecting in the same task
 * that mounts it leaves the user on a blank page for the whole navigation, because
 * the browser keeps showing the current document until the next one commits — and
 * an unpainted splash is a blank document.
 */
function nextPaint(): Promise<void> {
  return new Promise(resolve =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  );
}

export async function showBootSplash(): Promise<void> {
  if (typeof document === 'undefined' || document.getElementById(SPLASH_ELEMENT_ID)) {
    return;
  }

  const onStudyList = isStudyListRoute();

  const splash = document.createElement('div');
  splash.id = SPLASH_ELEMENT_ID;
  splash.className = `sycai-boot-splash ${
    onStudyList ? 'sycai-boot-splash--study-list' : 'sycai-boot-splash--viewer'
  }`;
  splash.setAttribute('role', 'status');
  splash.setAttribute('aria-live', 'polite');
  splash.setAttribute('data-cy', 'sycai-boot-splash');

  if (onStudyList) {
    splash.style.backgroundImage = studyListBackgroundImage();
  }

  document.body.append(splash);

  // SycaiLoadingIndicator itself, not a copy of it, so the splash and the in-app
  // loaders cannot drift apart. Its own classes place it at top-0 left-0, so it
  // needs the sizing className ViewerLayout also passes.
  createRoot(splash).render(<SycaiLoadingIndicator className="h-full w-full" />);

  await nextPaint();
}
